import { defineStore } from 'pinia'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type { ReferralRequestsState, ReferralRequest } from '@/types/referralRequests'
import type { Router } from 'vue-router'

const PAGE_SIZE = 20

export const useReferralRequestsStore = defineStore('referralRequests', {
  state: (): ReferralRequestsState => ({
    requests: [],
    loading: false,
    error: null,
    currentSort: '-created_at',
    pagination: {
      totalPages: 1,
      currentPage: 1
    },
    filters: {
      search: '',
      status: null,
      ineligible: false,
      unprocessed: false
    },
    totalCount: 0
  }),

  getters: {
    hasRequests: (state) => state.requests.length > 0,
    isPaginated: (state) => state.pagination.totalPages > 1
  },

  actions: {
    async fetchRequests({ page = 1 } = {}) {
      this.loading = true
      this.error = null
      try {
        const params: Record<string, any> = { page, page_size: PAGE_SIZE, ordering: this.currentSort }
        
        // Add search if present
        if (this.filters.search) {
          params.search = this.filters.search
        }

        // Add status filter if present
        if (this.filters.status) {
          params.status = this.filters.status
        }

        // Add ineligible filter if true
        if (this.filters.ineligible) {
          params.ineligible = true
        }

        // Add unprocessed filter if true
        if (this.filters.unprocessed) {
          params.unprocessed = true
        }

        const response = await api.get<{
          count: number
          next: string | null
          previous: string | null
          results: ReferralRequest[]
        }>(API_ROUTES.GET_REFERRAL_REQUESTS, { params })
        
        this.requests = response.data.results
        this.totalCount = response.data.count
        this.pagination = {
          totalPages: Math.ceil(response.data.count / PAGE_SIZE),
          currentPage: page
        }
      } catch (error) {
        console.error('Error fetching referral requests:', error)
        this.error = 'Failed to fetch referral requests'
        this.requests = []
        this.totalCount = 0
        this.pagination.totalPages = 1
      } finally {
        this.loading = false
      }
    },

    updateRoute(router: Router, updates: Record<string, string | undefined>) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push({
        query: {
          ...router.currentRoute.value.query,
          ...updates
        }
      })
    },

    async setSearch(router: Router, search: string) {
      this.filters.search = search
      this.updateRoute(router, { 
        search: search || undefined,
        page: '1'
      })
      await this.fetchRequests({ page: 1 })
    },

    async setPage(router: Router, page: number) {
      this.pagination.currentPage = page
      this.updateRoute(router, { page: page.toString() })
      await this.fetchRequests({ page })
    },

    async setFilter(router: Router, filterUpdates: { status?: string | null, ineligible?: boolean, unprocessed?: boolean }) {
      // Reset all filters first to ensure mutual exclusivity
      this.filters.status = null
      this.filters.ineligible = false
      this.filters.unprocessed = false

      // Apply new filter
      if (filterUpdates.status) this.filters.status = filterUpdates.status
      if (filterUpdates.ineligible) this.filters.ineligible = true
      if (filterUpdates.unprocessed) this.filters.unprocessed = true

      this.updateRoute(router, { 
        status: filterUpdates.status || undefined,
        ineligible: filterUpdates.ineligible ? 'true' : undefined,
        unprocessed: filterUpdates.unprocessed ? 'true' : undefined,
        requests_only: undefined, // Clear legacy param
        page: '1'
      })
      await this.fetchRequests({ page: 1 })
    },

    // Initialize store from URL params
    initFromUrl(query: Record<string, string>) {
      if (query.page) {
        this.pagination.currentPage = parseInt(query.page, 10)
      }
      if (query.search) {
        this.filters.search = query.search
      }
      if (query.status) {
        this.filters.status = query.status
      }
      if (query.ineligible === 'true') {
        this.filters.ineligible = true
      }
      if (query.unprocessed === 'true') {
        this.filters.unprocessed = true
      }
      if (typeof query.ordering === 'string' && query.ordering) {
        this.currentSort = query.ordering
      }
    },

    // Update referral status
    async updateReferralStatus(id: string, referralStatus: string) {
      try {
        const response = await api.patch(API_ROUTES.UPDATE_USER_APPLICATION(id), {
          referral_status: referralStatus
        })

        // Update the request in the local state
        const requestIndex = this.requests.findIndex(request => request.id === Number(id))
        if (requestIndex !== -1) {
          this.requests[requestIndex] = {
            ...this.requests[requestIndex],
            referral_status: referralStatus as any
          }
        }

        return response.data
      } catch (error) {
        console.error('Error updating referral status:', error)
        throw error
      }
    }
    ,
    updateSort(router: Router, field: string) {
      const current = this.currentSort
      const newSort = current === field
        ? `-${field}`
        : current === `-${field}`
          ? field
          : `-${field}`
      this.currentSort = newSort
      this.pagination.currentPage = 1
      // Update URL and then fetch
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push({
        query: {
          ...router.currentRoute.value.query,
          ordering: newSort,
          page: '1'
        }
      })
      return this.fetchRequests({ page: 1 })
    }
  }
}) 